import {
	Avatar,
	Box,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Pagination,
	styled,
	Typography,
} from "@mui/material";
import { types } from "api";
import { getInitials } from "helpers/authValidation";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getPath, routes } from "routes";

const PREFIX = "StudentList";

const classes = {
	root: `${PREFIX}-root`,
	link: `${PREFIX}-link`,
	listItem: `${PREFIX}-listItem`,
	avatar: `${PREFIX}-avatar`,
	list: `${PREFIX}-list`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.avatar}`]: {
		color: theme.palette.primary.contrastText,
		backgroundColor: theme.palette.secondary.main,
		padding: theme.spacing(3),
	},
	[`& .${classes.link}`]: {
		textDecoration: "none",
		color: "unset",
	},
	[`& .${classes.listItem}`]: {
		padding: theme.spacing(2),
		width: "300px",
		borderRadius: theme.shape.borderRadius,
		transitionDuration: "0.5s",

		"&:hover": {
			boxShadow: theme.shadows[2],
			pointer: "cursor",
		},
	},
	[`& .${classes.list}`]: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
	},
	[`& .${classes.root}`]: {
		backgroundColor: theme.palette.background.paper,
		minHeight: "100%",
		justifyContent: "space-between",
	},
}));

export interface Props {
	group?: types.Group;
}
export const StudentList = ({ group }: Props): JSX.Element => {
	const [display, setDisplay] = useState<types.UserBasic[]>([]);
	const index = useRef(0);
	const [pages, setPages] = useState(1);

	const updateDisplayStudents = useCallback((): void => {
		const start = index.current * 20;
		const end = start + 20;
		const displayGroups = group?.students.slice(start, end);
		setDisplay(displayGroups || []);
	}, [group?.students]);

	useEffect(() => {
		if (group?.students && group.students.length > 20) {
			updateDisplayStudents();
			setPages(Math.ceil(group?.students.length / 20));
			return;
		} else {
			setDisplay(group?.students || []);
		}
	}, [group?.students, updateDisplayStudents]);

	const handleChange = (
		event: React.ChangeEvent<unknown>,
		value: number,
	): void => {
		index.current = value - 1;
		if (group?.students) {
			updateDisplayStudents();
		}
	};

	return (
		<Root>
			<Box className={classes.root} display="flex" flexDirection="column" p={1}>
				<Box m={1}>
					<Typography variant="body1" align="left" color="textSecondary">
						STUDENT LIST
					</Typography>
					<List className={classes.list}>
						{display.map((value) => (
							<Link
								to={getPath(routes._.people._.userProfile, {
									userId: value.userId,
								})}
								className={classes.link}
								key={value.userId}
							>
								<ListItem className={classes.listItem}>
									<ListItemAvatar>
										<Avatar
											alt={`Avatar n°${value.userId}`}
											className={classes.avatar}
										>
											{value && getInitials(value.firstName, value.lastName)}
										</Avatar>
									</ListItemAvatar>
									<ListItemText
										id={value.userId}
										primary={`${value.firstName} ${value.lastName}`}
									/>
								</ListItem>
							</Link>
						))}
					</List>
				</Box>
				<Pagination
					count={pages}
					color="primary"
					onChange={handleChange}
					style={{ alignSelf: "center" }}
				/>
			</Box>
		</Root>
	);
};
